import { useCallback, useEffect, useState } from "react";
import { ImagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { API_BASE, formatApiError } from "@/lib/api";

const EMPTY = { name: "", quote: "", photo_path: null };

export default function TestimonialsPanel({ toast }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/testimonials");
            setItems(res.data);
        } catch (err) {
            toast({
                title: "Gagal memuat testimoni",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const startNew = () => {
        setEditing("new");
        setForm(EMPTY);
        setFile(null);
        setPreview(null);
    };

    const startEdit = (t) => {
        setEditing(t.id);
        setForm({ name: t.name, quote: t.quote, photo_path: t.photo_path });
        setFile(null);
        setPreview(t.photo_path ? `${API_BASE}/files/${t.photo_path}` : null);
    };

    const cancel = () => {
        setEditing(null);
        setForm(EMPTY);
        setFile(null);
        setPreview(null);
    };

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const save = async () => {
        if (form.name.trim().length < 2 || form.quote.trim().length < 5) {
            toast({
                title: "Lengkapi dulu ya",
                description: "Nama minimal 2 karakter, testimoni minimal 5 karakter.",
                variant: "destructive",
            });
            return;
        }
        setSaving(true);
        try {
            let photo_path = form.photo_path;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/admin/testimonials-photo", fd);
                photo_path = up.data.path;
            }
            const payload = {
                name: form.name.trim(),
                quote: form.quote.trim(),
                photo_path: photo_path || null,
            };
            if (editing === "new") {
                await api.post("/admin/testimonials", payload);
                toast({ title: "Testimoni ditambahkan" });
            } else {
                await api.patch(`/admin/testimonials/${editing}`, payload);
                toast({ title: "Testimoni diperbarui" });
            }
            cancel();
            load();
        } catch (err) {
            toast({
                title: "Gagal menyimpan testimoni",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Hapus testimoni dari ${t.name}?`)) return;
        try {
            await api.delete(`/admin/testimonials/${t.id}`);
            toast({ title: "Testimoni dihapus" });
            load();
        } catch (err) {
            toast({
                title: "Gagal menghapus testimoni",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    return (
        <div data-testid="testimonials-panel">
            {editing ? (
                <div
                    data-testid="testimonial-form"
                    className="rounded-3xl border border-border bg-white p-8 shadow-brand"
                >
                    <h3 className="font-display text-xl font-extrabold text-ink">
                        {editing === "new" ? "Tambah Testimoni" : "Ubah Testimoni"}
                    </h3>

                    <div className="mt-6 space-y-5">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label className="text-sm font-bold text-ink/80">
                                    Nama Pelanggan
                                </Label>
                                <Input
                                    data-testid="testimonial-input-name"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, name: e.target.value }))
                                    }
                                    placeholder="Contoh: Ibu Sari"
                                    className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-bold text-ink/80">
                                    Foto (opsional, maks. 5 MB)
                                </Label>
                                <label className="flex h-12 cursor-pointer items-center gap-2 rounded-xl border border-ink/10 bg-[#F9FAFB] px-4 text-sm font-semibold text-ink/60 transition-colors hover:border-brand hover:text-brand">
                                    <ImagePlus size={17} />
                                    {file ? file.name : "Pilih foto..."}
                                    <input
                                        data-testid="testimonial-input-photo"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        className="hidden"
                                        onChange={onFileChange}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                Testimoni
                            </Label>
                            <Textarea
                                data-testid="testimonial-input-quote"
                                rows={3}
                                value={form.quote}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, quote: e.target.value }))
                                }
                                placeholder="Tulis testimoni pelanggan di sini..."
                                className="rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>

                        {preview && (
                            <div className="flex items-center gap-3">
                                <img
                                    src={preview}
                                    alt="Pratinjau foto"
                                    data-testid="testimonial-photo-preview"
                                    className="h-16 w-16 rounded-full object-cover ring-2 ring-brand/20"
                                />
                                <span className="text-xs text-ink/40">
                                    Pratinjau foto yang akan ditampilkan
                                </span>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <Button
                                data-testid="testimonial-save-button"
                                onClick={save}
                                disabled={saving}
                                className="h-11 rounded-full bg-brand px-8 font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                            >
                                {saving ? "Menyimpan..." : "Simpan"}
                            </Button>
                            <Button
                                data-testid="testimonial-cancel-button"
                                variant="outline"
                                onClick={cancel}
                                className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                            >
                                Batal
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex justify-end">
                        <Button
                            data-testid="testimonial-add-button"
                            onClick={startNew}
                            className="h-11 rounded-full bg-brand px-6 font-bold text-white hover:bg-brand-deep"
                        >
                            <Plus size={17} className="mr-1.5" />
                            Tambah Testimoni
                        </Button>
                    </div>

                    {loading ? (
                        <p className="mt-6 py-14 text-center text-sm text-ink/50">
                            Memuat testimoni...
                        </p>
                    ) : items.length === 0 ? (
                        <div
                            data-testid="testimonials-empty"
                            className="mt-6 rounded-3xl border border-border bg-white py-14 text-center shadow-brand"
                        >
                            <p className="font-display text-lg font-bold text-ink">
                                Belum ada testimoni
                            </p>
                            <p className="mx-auto mt-1 max-w-sm text-sm text-ink/50">
                                Tambahkan testimoni pelanggan — foto, nama, dan
                                katanya akan tampil otomatis di halaman utama.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                            {items.map((t) => (
                                <div
                                    key={t.id}
                                    data-testid={`admin-testimonial-${t.id}`}
                                    className="flex flex-col rounded-3xl border border-border bg-white p-6 shadow-brand"
                                >
                                    <div className="flex items-center gap-3">
                                        {t.photo_path ? (
                                            <img
                                                src={`${API_BASE}/files/${t.photo_path}`}
                                                alt={t.name}
                                                className="h-11 w-11 rounded-full object-cover"
                                            />
                                        ) : (
                                            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand font-display font-bold text-white">
                                                {t.name.charAt(0).toUpperCase()}
                                            </span>
                                        )}
                                        <p className="font-bold text-ink">{t.name}</p>
                                    </div>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/60">
                                        &ldquo;{t.quote}&rdquo;
                                    </p>
                                    <div className="mt-4 flex gap-2 border-t border-border pt-4">
                                        <Button
                                            data-testid={`testimonial-edit-${t.id}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startEdit(t)}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                        >
                                            <Pencil size={13} className="mr-1" />
                                            Ubah
                                        </Button>
                                        <Button
                                            data-testid={`testimonial-delete-${t.id}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => remove(t)}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-red-50 hover:text-red-600"
                                        >
                                            <Trash2 size={13} className="mr-1" />
                                            Hapus
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
